import { useState } from 'react';
import { ChevronDown, RefreshCw, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import type { ProvisionDeploymentDto } from '@/services/provisioning';

const generatePassword = (): string => {
    const alphabet =
        'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%^&*';
    const bytes = new Uint32Array(20);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (n) => alphabet[n % alphabet.length]).join('');
};

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    defaultSshUser: string;
    hasStoredCredential: boolean;
    isRetry: boolean;
    isPending: boolean;
    /** Account owner — who actually receives the credentials once live. */
    notifyEmail?: string;
    onSubmit: (dto: ProvisionDeploymentDto) => void;
}

export function ProvisionDialog({
    open,
    onOpenChange,
    defaultSshUser,
    hasStoredCredential,
    isRetry,
    isPending,
    notifyEmail,
    onSubmit,
}: Props) {
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [sshUser, setSshUser] = useState(defaultSshUser);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [credType, setCredType] = useState<'PASSWORD' | 'PRIVATE_KEY'>('PRIVATE_KEY');
    const [credSecret, setCredSecret] = useState('');
    const [persist, setPersist] = useState(false);

    const canSubmit =
        adminEmail.trim().length > 3 && adminPassword.length >= 8 && sshUser.trim().length > 0;

    const submit = () => {
        onSubmit({
            adminEmail: adminEmail.trim(),
            adminPassword,
            sshUser: sshUser.trim(),
            sshCredential: credSecret.trim()
                ? { type: credType, secret: credSecret }
                : undefined,
            persistSshCredential: persist,
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {isRetry ? 'Retry provisioning' : 'Provision this deployment'}
                    </DialogTitle>
                    <DialogDescription>
                        Installs Docker, ships the tenant stack and brings it up over SSH, then
                        waits for the domains to answer before going live. The credentials below
                        seed the tenant's own admin account. Once the deployment is confirmed
                        live they are emailed to the account owner
                        {notifyEmail ? <> — <span className="font-medium">{notifyEmail}</span></> : null}, not
                        to the address below.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="adminEmail">Tenant admin login</Label>
                        <Input
                            id="adminEmail"
                            type="email"
                            value={adminEmail}
                            onChange={(e) => setAdminEmail(e.target.value)}
                            placeholder="admin@customer.com"
                        />
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            The sign-in address for the new tenant, created in its own database.
                            Nothing is sent here.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="adminPassword">Tenant admin password</Label>
                        <div className="flex gap-2">
                            <Input
                                id="adminPassword"
                                type="text"
                                value={adminPassword}
                                onChange={(e) => setAdminPassword(e.target.value)}
                                placeholder="At least 8 characters"
                                className="font-mono"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setAdminPassword(generatePassword())}
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Generate
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="sshUser">SSH user</Label>
                        <Input
                            id="sshUser"
                            value={sshUser}
                            onChange={(e) => setSshUser(e.target.value)}
                            className="font-mono"
                        />
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {hasStoredCredential
                                ? 'This host has a saved SSH credential, which will be used.'
                                : "Authenticates with the provisioner's fleet key. The user needs passwordless sudo."}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowAdvanced((v) => !v)}
                        className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                    >
                        <ChevronDown
                            className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                        />
                        This host can't accept the fleet key
                    </button>

                    {showAdvanced && (
                        <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-800">
                            <RadioGroup
                                value={credType}
                                onValueChange={(v) => setCredType(v as 'PASSWORD' | 'PRIVATE_KEY')}
                                className="flex gap-4"
                            >
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="PRIVATE_KEY" id="cred-key" />
                                    <Label htmlFor="cred-key" className="font-normal">
                                        Private key
                                    </Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="PASSWORD" id="cred-pw" />
                                    <Label htmlFor="cred-pw" className="font-normal">
                                        Password
                                    </Label>
                                </div>
                            </RadioGroup>

                            <Textarea
                                value={credSecret}
                                onChange={(e) => setCredSecret(e.target.value)}
                                rows={credType === 'PRIVATE_KEY' ? 5 : 1}
                                placeholder={
                                    credType === 'PRIVATE_KEY'
                                        ? '-----BEGIN OPENSSH PRIVATE KEY-----'
                                        : 'SSH password'
                                }
                                className="font-mono text-xs"
                            />

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="persist"
                                    checked={persist}
                                    onCheckedChange={(c) => setPersist(c === true)}
                                />
                                <Label htmlFor="persist" className="font-normal">
                                    Save this credential (encrypted) for future runs
                                </Label>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={!canSubmit || isPending}>
                        <Rocket className="mr-2 h-4 w-4" />
                        {isPending ? 'Starting…' : 'Start provisioning'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
